import { useSearchParams } from "react-router";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface PaginationControlsProps {
  page: number; // 0-indexed
  totalPages: number;
  totalItems: number;
}

export function PaginationControls({ page, totalPages, totalItems }: PaginationControlsProps) {
  const [, setSearchParams] = useSearchParams();

  const goToPage = (next: number) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (next === 0) {
          params.delete("page");
        } else {
          params.set("page", String(next));
        }
        return params;
      },
      { replace: false },
    );
  };

  if (totalPages <= 1) {
    return totalItems > 0 ? (
      <p className="text-center text-sm text-muted-foreground">Hiển thị {totalItems} kết quả</p>
    ) : null;
  }

  const previousDisabled = page <= 0;
  const nextDisabled = page >= totalPages - 1;
  const pageList = buildPageList(page, totalPages);

  return (
    <nav
      aria-label="Phân trang"
      className="flex flex-col items-center justify-between gap-3 sm:flex-row"
    >
      <p className="order-2 text-xs text-muted-foreground sm:order-1">
        Tổng {totalItems} kết quả · Trang {page + 1} / {totalPages}
      </p>

      <div className="order-1 flex w-full items-center justify-between gap-2 sm:order-2 sm:w-auto">
        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(page - 1)}
          disabled={previousDisabled}
          aria-label="Trang trước"
        >
          <ChevronLeft aria-hidden="true" />
          <span className="hidden sm:inline">Trước</span>
        </Button>

        <span className="text-sm sm:hidden">
          Trang {page + 1} / {totalPages}
        </span>

        <div className="hidden items-center gap-1 sm:flex">
          {pageList.map((entry, idx) =>
            entry === "ellipsis" ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
                …
              </span>
            ) : (
              <Button
                key={entry}
                variant={entry === page ? "default" : "outline"}
                size="sm"
                onClick={() => goToPage(entry)}
                aria-current={entry === page ? "page" : undefined}
                className={cn("min-w-9", entry === page && "pointer-events-none")}
              >
                {entry + 1}
              </Button>
            ),
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => goToPage(page + 1)}
          disabled={nextDisabled}
          aria-label="Trang sau"
        >
          <span className="hidden sm:inline">Sau</span>
          <ChevronRight aria-hidden="true" />
        </Button>
      </div>
    </nav>
  );
}

type PageEntry = number | "ellipsis";

function buildPageList(current: number, total: number): PageEntry[] {
  const pages = new Set<number>();
  pages.add(0);
  pages.add(total - 1);
  for (let p = current - 2; p <= current + 2; p += 1) {
    if (p >= 0 && p < total) pages.add(p);
  }
  const sorted = Array.from(pages).sort((a, b) => a - b);
  const result: PageEntry[] = [];
  for (let i = 0; i < sorted.length; i += 1) {
    const value = sorted[i];
    if (value === undefined) continue;
    if (i > 0) {
      const prev = sorted[i - 1];
      if (prev !== undefined && value - prev > 1) result.push("ellipsis");
    }
    result.push(value);
  }
  return result;
}
